import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { FaShieldAlt, FaPlus, FaPen, FaArchive, FaEye, FaEyeSlash, FaUpload, FaFileAlt, FaDownload, FaLock, FaExternalLinkAlt, FaExpand, FaStar, FaRegStar, FaSitemap, FaChevronDown, FaChevronRight, FaSlidersH } from "react-icons/fa";
import { PERSONAL_VAULT_CONSTANTS } from "../_MODULES/_API";
import { PersonalVaultEntryFormModal } from "../_UI/PersonalVaultEntryFormModal";
import { PersonalVaultRecoveryPanel } from "../_UI/PersonalVaultRecoveryPanel";
import { CopyValue } from "../_UI/CopyValue";
import { OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, OPTIONS_VAULT_KIND, OPTIONS_PERSONAL_VAULT_AUTH_MODE, onFindOptionLabel } from "../../../_Catalogs.Views";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, EmptyState, Spinner, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldInput, FieldFile, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { FORMAT_API_ERROR_MESSAGE } from "../../../../../_CORE/API/_ApiUtil/handleAxiosError";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { PrivateBlobMedia } from "../../../../_UI/PrivateBlobMedia";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { CredentialExportPickerModal } from "../../../../_UI/CredentialExportPickerModal";
import { downloadCredentialTxt, downloadCredentialPdf, onStripHtml } from "../../../../_UI/CredentialDownload";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { NestedVaultModal } from "../../../../_UI/NestedVaultModal";
import { VaultNestedEntryCard } from "../../../../_UI/VaultNestedEntryCard";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import "../../../../_UI/_Styles/FileCollection.css";
import "../_UI/_Styles/PersonalVault.css";
import "../../../../_UI/_Styles/CredentialVaultParity.css";

const onSafeExternalUrl = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;

    try {
        const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        const parsed = new URL(candidate);
        const host = String(parsed.hostname ?? "").trim();
        if (!host || (!host.includes(".") && host !== "localhost")) return null;
        return parsed.href;
    } catch {
        return null;
    }
};

const ROWS = [
    { field: "environment", label: "Entorno", isSecret: false },
    { field: "url", label: "Panel o dirección", isSecret: false },
    { field: "email", label: "Correo", isSecret: false },
    { field: "username", label: "Usuario", isSecret: false },
    { field: "accountId", label: "ID / número de cliente", isSecret: false },
    { field: "phone", label: "Teléfono asociado", isSecret: false },
    { field: "secret", label: "Contraseña", isSecret: true },
    { field: "passwordHint", label: "Pista / palabra secreta", isSecret: false },
    { field: "securityPin", label: "PIN de la cuenta", isSecret: true },
    { field: "recoveryEmail", label: "Email de recuperación", isSecret: false },
    { field: "recoveryPhone", label: "Teléfono de recuperación", isSecret: false },
    { field: "authSecret", label: "Secreto TOTP / 2FA", isSecret: true },
    { field: "securityNotes", label: "Notas de seguridad", isSecret: true },
    { field: "passwordChangedAt", label: "Último cambio de contraseña", isSecret: false },
    { field: "passwordExpiresAt", label: "Rotar / expira el", isSecret: false },
    { field: "host", label: "Host o IP", isSecret: false },
    { field: "port", label: "Puerto", isSecret: false },
    { field: "databaseName", label: "Base de datos", isSecret: false },
    { field: "token", label: "Token o API key", isSecret: true },
    { field: "connectionString", label: "Cadena de conexión", isSecret: true },
    { field: "sshKey", label: "Clave privada / SSH", isSecret: true },
];


const onFlattenEntries = (entries, level = 0) => entries.flatMap((entry) => [
    { ...entry, level },
    ...onFlattenEntries(entry.children ?? [], level + 1),
]);

const onSortFavoriteTree = (entries = []) => [...entries]
    .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || Number(a.position ?? 0) - Number(b.position ?? 0))
    .map((entry) => ({ ...entry, children: onSortFavoriteTree(entry.children ?? []) }));

const onPatchEntryTree = (entries = [], id, patch) => onSortFavoriteTree(entries.map((entry) => ({
    ...entry,
    ...(String(entry.id) === String(id) ? patch : {}),
    children: onPatchEntryTree(entry.children ?? [], id, patch),
})));

const onRawFlatEntries = (entries = []) => entries.flatMap(({ children = [], level, ...entry }) => [
    { ...entry },
    ...onRawFlatEntries(children),
]);

const onBuildEntryTree = (rows = []) => {
    const mapped = new Map(rows.map((row) => [String(row.id), { ...row, children: [] }]));
    const roots = [];

    mapped.forEach((row) => {
        const parentId = row.parentId ? String(row.parentId) : "";
        const parent = parentId && parentId !== String(row.id) ? mapped.get(parentId) : null;
        if (parent) parent.children.push(row);
        else roots.push(row);
    });

    return onSortFavoriteTree(roots);
};

const onFindEntryTree = (entries = [], id) => {
    for (const entry of entries) {
        if (String(entry.id) === String(id)) return entry;
        const nested = onFindEntryTree(entry.children ?? [], id);
        if (nested) return nested;
    }
    return null;
};

const onUpsertEntryTree = (entries = [], saved = {}) => {
    if (!saved?.id) return entries;
    const rows = onRawFlatEntries(entries);
    const index = rows.findIndex((row) => String(row.id) === String(saved.id));
    const previous = index >= 0 ? rows[index] : null;
    const merged = {
        ...(previous ?? {}),
        ...saved,
        files: saved.files ?? previous?.files ?? [],
    };
    if (index >= 0) rows[index] = merged;
    else rows.push(merged);
    return onBuildEntryTree(rows);
};

const onCollectEntrySubtree = (entry) => entry ? [
    entry,
    ...(entry.children ?? []).flatMap((child) => onCollectEntrySubtree(child)),
] : [];

const onRemoveEntrySubtree = (entries = [], id) => {
    const target = onFindEntryTree(entries, id);
    if (!target) return entries;
    const removeIds = new Set(onCollectEntrySubtree(target).map((entry) => String(entry.id)));
    return onBuildEntryTree(onRawFlatEntries(entries).filter((entry) => !removeIds.has(String(entry.id))));
};

export const PersonalVaultGroupDetailViewMain = () => {

    const { groupId } = useParams();

    const navigate = useNavigate();

    const [usePin, setPin] = useState("");
    const [useUnlockedPin, setUnlockedPin] = useState(null);
    const [usePinError, setPinError] = useState(null);
    const [useRevealed, setRevealed] = useState({});
    const [useFiles, setFiles] = useState([]);
    const [useIsUploading, setIsUploading] = useState(false);
    const [useEntryEdit, setEntryEdit] = useState(null);
    const [useParentDefault, setParentDefault] = useState(null);
    const [usePreviewFile, setPreviewFile] = useState(null);
    const [useExportEntry, setExportEntry] = useState(null);
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [useTypeFilter, setTypeFilter] = useState("ALL");
    const [useFavoriteFilter, setFavoriteFilter] = useState("ALL");
    const [useEnvironmentFilter, setEnvironmentFilter] = useState("");
    const [useIsGroupFilesOpen, setIsGroupFilesOpen] = useState(false);
    const [useNestedEntryId, setNestedEntryId] = useState(null);
    const [useCardExpanded, setCardExpanded] = useState({});
    const [useDownloadingFilesKey, setDownloadingFilesKey] = useState("");

    const [useIsEntryOpen, setIsEntryOpen] = useState(false);

    const { apiGet: onGetGroup, useApiData: GROUP, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    const ROOT_ENTRIES = useMemo(() => GROUP?.entries ?? [], [GROUP]);
    const ALL_ENTRIES = useMemo(() => onFlattenEntries(ROOT_ENTRIES), [ROOT_ENTRIES]);

    const ENTRIES = useMemo(() => {
        const onEntryMatches = (entry) => {
            if (useTypeFilter !== "ALL" && entry.type !== useTypeFilter) return false;
            if (useFavoriteFilter !== "ALL" && (!!entry.isFavorite !== (useFavoriteFilter === "YES"))) return false;
            if (String(useEnvironmentFilter || "").trim() && !onMatchesSearch(useEnvironmentFilter, [entry.environment])) return false;
            return onMatchesSearch(useSearch, [
                entry.title, entry.email, entry.username, entry.host, entry.port, entry.url,
                entry.databaseName, entry.environment, entry.command, entry.notes, entry.authMode,
                entry.authIssuer, entry.authAccount, entry.recoveryEmail, entry.recoveryPhone,
                ...(entry.extra ?? []).flatMap((field) => [field.label, field.isSecret ? "" : field.value]),
            ]);
        };
        return ROOT_ENTRIES.filter((root) => onFlattenEntries([root]).some(onEntryMatches));
    }, [ROOT_ENTRIES, useSearch, useTypeFilter, useFavoriteFilter, useEnvironmentFilter]);

    const ADVANCED_COUNT = [useTypeFilter !== "ALL", useFavoriteFilter !== "ALL", String(useEnvironmentFilter || "").trim()].filter(Boolean).length;

    const NESTED_ENTRY = useMemo(() => (
        useNestedEntryId ? ROOT_ENTRIES.find((entry) => String(entry.id) === String(useNestedEntryId)) ?? null : null
    ), [ROOT_ENTRIES, useNestedEntryId]);

    const PAGER = usePagedList(ENTRIES, 6, "accessPage");

    const onLoadGroup = () => onGetGroup({
        endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_DETAIL, isDetail: true, config: { pathParams: { id: groupId } },
    });

    useEffect(() => { onLoadGroup(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Los secretos revelados desaparecen al cambiar de pestaña/minimizar.
    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) setRevealed({});
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    // Un PIN desbloqueado vive sólo en memoria y se auto-bloquea tras 15 minutos.
    useEffect(() => {
        if (!useUnlockedPin) return undefined;
        const timer = window.setTimeout(() => {
            setUnlockedPin(null);
            setPin("");
            setRevealed({});
        }, 15 * 60 * 1000);
        return () => window.clearTimeout(timer);
    }, [useUnlockedPin]);

    const onVerifyPin = async () => {

        const PIN = usePin.trim();

        if (!PIN) {
            setPinError("Escribí el pin del grupo");
            return;
        }

        const URL_RESOURCE = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.GROUP_VERIFY_PIN].replace(":id", groupId);

        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(URL_RESOURCE, { accessPin: PIN });

        if (RESPONSE?.success === false) {
            const message = FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudo verificar el PIN.");
            setPinError(RESPONSE?.message || "No se pudo verificar el PIN");
            REACT_HOT_TOAST_ERROR(message, { duration: 7000 });
            return;
        }

        if (!RESPONSE?.data?.isValid) {
            setPinError("El pin no es correcto");
            return;
        }

        setPinError(null);
        setUnlockedPin(PIN);
    };

    const onRevealData = async (entry) => {
        if (useRevealed[entry.id]) return useRevealed[entry.id];

        const URL_RESOURCE = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.ENTRY_REVEAL].replace(":id", entry.id);
        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(URL_RESOURCE, { accessPin: useUnlockedPin ?? undefined });

        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudo revelar el acceso."), { duration: 7000 });
            return null;
        }

        const DATA = RESPONSE?.data ?? null;
        if (DATA) {
            setRevealed((prev) => ({ ...prev, [entry.id]: DATA }));
            window.setTimeout(() => {
                setRevealed((prev) => ({ ...prev, [entry.id]: null }));
            }, 60000);
        }
        return DATA;
    };

    const onReveal = async (entry) => {
        if (useRevealed[entry.id]) {
            setRevealed((prev) => ({ ...prev, [entry.id]: null }));
            return;
        }
        await onRevealData(entry);
    };

    const onUpload = async (entryId = null) => {

        if (useFiles.length === 0) return false;

        setIsUploading(true);

        const FORM_DATA = new FormData();
        FORM_DATA.append("groupId", groupId);
        FORM_DATA.append("title", "Archivo");
        FORM_DATA.append("purpose", "GENERAL");
        if (useUnlockedPin) FORM_DATA.append("accessPin", useUnlockedPin);
        if (entryId) FORM_DATA.append("entryId", entryId);
        useFiles.forEach((file) => FORM_DATA.append("files[]", file));

        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.FILE_UPLOAD], FORM_DATA);

        setIsUploading(false);

        if (RESPONSE?.success === false) {
            REACT_HOT_TOAST_ERROR(FORMAT_API_ERROR_MESSAGE(RESPONSE, "No se pudieron subir los archivos."), { duration: 7000 });
            return false;
        }

        const savedFiles = Array.isArray(RESPONSE?.data) ? RESPONSE.data : [];
        setFiles([]);

        if (entryId) {
            const owner = onFindEntryTree(GROUP?.entries ?? [], entryId);
            mutateLocal({
                id: GROUP.id,
                patch: {
                    entries: onPatchEntryTree(GROUP.entries ?? [], entryId, {
                        files: [...(owner?.files ?? []), ...savedFiles],
                    }),
                },
            });
        } else {
            mutateLocal({ id: GROUP.id, patch: { files: [...savedFiles, ...(GROUP?.files ?? [])] } });
        }

        return true;
    };

    const onLoadFileBlob = useCallback(async (file) => {
        if (!file?.id) throw new Error("Archivo sin identificador");
        const URL_RESOURCE = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.FILE_DOWNLOAD].replace(":id", file.id);
        const BLOB = await API_CORE.SERVICES.POST_RESOURCE(
            URL_RESOURCE,
            { accessPin: useUnlockedPin ?? undefined },
            { responseType: "blob" },
        );

        if (!(BLOB instanceof Blob)) throw new Error("No se pudo obtener el archivo");
        return BLOB;
    }, [useUnlockedPin]);

    const onDownload = async (file) => {
        const BLOB = await onLoadFileBlob(file);
        const URL_OBJECT = window.URL.createObjectURL(BLOB);
        const LINK = window.document.createElement("a");

        LINK.href = URL_OBJECT;
        LINK.download = file.originalName || file.title;
        window.document.body.appendChild(LINK);
        LINK.click();
        window.document.body.removeChild(LINK);
        window.URL.revokeObjectURL(URL_OBJECT);
    };

    const onDownloadAllFiles = async (files, label, key) => {
        const list = Array.isArray(files) ? files.filter(Boolean) : [];
        if (!list.length || useDownloadingFilesKey) return;
        setDownloadingFilesKey(key);
        try {
            await downloadFilesAsZip({
                files: list,
                loadBlob: onLoadFileBlob,
                fileName: `${label || "archivos"}-archivos.zip`,
                successMessage: `${list.length} archivo${list.length === 1 ? "" : "s"} descargado${list.length === 1 ? "" : "s"}`,
            });
        } finally {
            setDownloadingFilesKey("");
        }
    };

    const onArchiveFile = async (file) => {

        const IS_CONFIRMED = await confirm.danger("Archivar archivo", `Se archiva "${file.title}". Podés restaurarlo desde Archivados.`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: PERSONAL_VAULT_CONSTANTS.FILE_ARCHIVE,
            pathParams: { id: file.id }, data: { accessPin: useUnlockedPin ?? undefined },
            successMessage: "Archivo archivado",
        });

        if (RESPONSE.success) {
            if (file.entryId) {
                const owner = onFindEntryTree(GROUP?.entries ?? [], file.entryId);
                mutateLocal({
                    id: GROUP.id,
                    patch: {
                        entries: onPatchEntryTree(GROUP.entries ?? [], file.entryId, {
                            files: (owner?.files ?? []).filter((item) => String(item.id) !== String(file.id)),
                        }),
                    },
                });
            } else {
                mutateLocal({
                    id: GROUP.id,
                    patch: { files: (GROUP?.files ?? []).filter((item) => String(item.id) !== String(file.id)) },
                });
            }
        }
    };

    const onToggleFavorite = async (entry) => {
        const previous = !!entry.isFavorite;
        const next = !previous;
        const patchGroupEntries = (value) => mutateLocal({
            id: GROUP.id,
            patch: { entries: onPatchEntryTree(GROUP.entries ?? [], entry.id, { isFavorite: value }) },
        });

        patchGroupEntries(next);

        const RESPONSE = await onSilentPatch({
            endpoint: PERSONAL_VAULT_CONSTANTS.ENTRY_UPDATE, id: entry.id,
            data: { isFavorite: next, accessPin: useUnlockedPin ?? undefined },
            errorMessage: "No se pudo guardar el destacado del acceso.",
        });

        if (!RESPONSE.success) patchGroupEntries(previous);
    };

    const onArchiveEntry = async (entry) => {

        const IS_CONFIRMED = await confirm.danger("Archivar acceso", `Se archiva "${entry.title}" y sus accesos anidados. Podés restaurarlo desde Archivados.`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: PERSONAL_VAULT_CONSTANTS.ENTRY_ARCHIVE,
            pathParams: { id: entry.id }, data: { accessPin: useUnlockedPin ?? undefined },
            successMessage: "Acceso archivado",
        });

        if (RESPONSE.success) {
            if (String(useNestedEntryId ?? "") === String(entry.id)) setNestedEntryId(null);
            const target = onFindEntryTree(GROUP?.entries ?? [], entry.id);
            mutateLocal({
                id: GROUP.id,
                patch: { entries: onRemoveEntrySubtree(GROUP.entries ?? [], entry.id) },
            });
            setRevealed((prev) => {
                const nextState = { ...prev };
                onCollectEntrySubtree(target).forEach((item) => delete nextState[item.id]);
                return nextState;
            });
        }
    };

    const onCredentialRows = (entry, revealed) => {
        const rows = [
            { key: "type", label: "Tipo", value: onFindOptionLabel(OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, entry.type), isSecret: false },
            { key: "authMode", label: "Modo de autenticación", value: onFindOptionLabel(OPTIONS_PERSONAL_VAULT_AUTH_MODE, entry.authMode) || entry.authMode, isSecret: false },
            ...ROWS.map((row) => ({
                key: row.field,
                label: row.field === "secret" ? "Contraseña real" : row.label,
                // La pista se guarda cifrada, pero en exportación se trata como dato para compartir.
                isSecret: row.field === "passwordHint" ? false : !!row.isSecret,
                value: row.isSecret ? (revealed?.[row.field] ?? "") : (entry[row.field] ?? ""),
            })),
            ...(entry.extra ?? []).map((field, index) => ({
                key: `extra_${index}`,
                label: field.label,
                isSecret: !!field.isSecret,
                value: field.isSecret
                    ? (revealed?.extra?.find((item) => item.label === field.label)?.value ?? "")
                    : field.value,
            })),
            { key: "command", label: "Comando de conexión", value: entry.command ?? "", isSecret: false },
            { key: "notes", label: "Notas", value: onStripHtml(entry.notes), isSecret: false },
            { key: "files", label: "Archivos", value: (entry.files ?? []).map((file) => file.originalName || file.title).filter(Boolean).join(", "), isSecret: false },
        ];
        return rows.filter((row) => row.value !== null && row.value !== undefined && String(row.value) !== "");
    };

    const onOpenExport = async (entry) => {
        const revealed = await onRevealData(entry);
        if (!revealed) return;
        setExportEntry({ entry, rows: onCredentialRows(entry, revealed) });
    };

    const onExportCredential = (format, rows) => {
        if (!useExportEntry || !rows?.length) return;
        const payload = { title: useExportEntry.entry.title, rows, fileName: useExportEntry.entry.title };
        if (format === "pdf") downloadCredentialPdf(payload);
        else downloadCredentialTxt(payload);
        setExportEntry(null);
    };

    if (IS_LOADING) return <Spinner label="Cargando grupo personal…" />;

    if (!GROUP) return <EmptyState icon={<FaShieldAlt />} title="No encontramos ese grupo personal" />;

    const IS_LOCKED = GROUP.hasAccessPin && !useUnlockedPin;

    const PARENT_OPTIONS = ROOT_ENTRIES.map((entry) => ({
        value: entry.id,
        label: entry.title,
    }));

    const onEntrySavedLocal = (saved, isEdit = false) => {
        if (!saved?.id) return;
        mutateLocal({ id: GROUP.id, patch: { entries: onUpsertEntryTree(GROUP.entries ?? [], saved) } });
        if (!isEdit && saved.parentId && !useNestedEntryId) setNestedEntryId(saved.parentId);
        setEntryEdit(null);
        setParentDefault(null);
    };

    const onOpenCreate = (parentId = null) => {
        const requestedParent = parentId ? onFindEntryTree(ROOT_ENTRIES, parentId) : null;
        const normalizedParentId = requestedParent?.parentId ? requestedParent.parentId : parentId;
        setEntryEdit(null);
        setParentDefault(normalizedParentId);
        setIsEntryOpen(true);
    };

    const GROUP_FILES = (GROUP.files ?? []).filter((file) => !file.entryId);

    const onOpenViewer = (file) => setPreviewFile(file);

    return (
        <ViewLayout
            header={
                <PageHeader
                title={GROUP.name}
                description={GROUP.description}
                icon={<FaShieldAlt />}
                backTo="/personal-vault"
                status={<Badge tone="neutral">{onFindOptionLabel(OPTIONS_VAULT_KIND, GROUP.kind)}</Badge>}
                breadcrumbs={[
                    {
                        label: "Bóveda personal", href: "/personal-vault",
                        onClick: (event) => { event.preventDefault(); navigate("/personal-vault"); },
                    },
                    { label: GROUP.name },
                ]}
                    actions={
                        !IS_LOCKED && (
                            <>
                                {GROUP.hasAccessPin && (
                                    <AppButton variant="ghost" tone="neutral" icon={<FaLock />}
                                        onClick={() => { setUnlockedPin(null); setPin(""); setRevealed({}); }}>
                                        Bloquear grupo
                                    </AppButton>
                                )}
                                <AppButton
                                    className="LStudio__app_vault_header_file_button"
                                    variant="soft"
                                    tone="neutral"
                                    icon={<FaUpload />}
                                    onClick={() => setIsGroupFilesOpen(true)}
                                >
                                    Agregar archivo
                                </AppButton>
                                <AppButton icon={<FaPlus />} onClick={() => onOpenCreate()}>
                                    Nuevo acceso
                                </AppButton>
                            </>
                        )
                    } />
            } >

            <div className="LStudio__app_vault_detail_stack">

            {
                GROUP.url && (
                    onSafeExternalUrl(GROUP.url)
                        ? (
                            <a href={onSafeExternalUrl(GROUP.url)} target="_blank" rel="noreferrer" className="LStudio__app_vault_detail_url">
                                {GROUP.url} <FaExternalLinkAlt />
                            </a>
                        )
                        : <span className="LStudio__app_vault_detail_url LStudio__app_vault_detail_url--plain">{GROUP.url}</span>
                )
            }

            {
                IS_LOCKED ? (
                    <div className="LStudio__app_lock">
                        <div className="LStudio__app_lock_card">

                            <span className="LStudio__app_lock_icon"><FaLock /></span>

                            <h2 className="LStudio__app_lock_title">Grupo protegido</h2>

                            <p className="LStudio__app_lock_text">
                                Este grupo personal pide un pin para mostrar sus accesos.
                            </p>

                            <FieldInput id="vaultPin" type="pin" label="Pin del grupo" placeholder="••••"
                                value={usePin} error={usePinError}
                                onChange={(event) => setPin(event.target.value)}
                                onKeyDown={(event) => { if (event.key === "Enter") onVerifyPin(); }} />

                            <div className="LStudio__app_lock_actions">
                                <AppButton icon={<FaLock />} onClick={onVerifyPin}>Abrir grupo</AppButton>
                            </div>

                        </div>
                    </div>
                ) : (
                    <>
                        {
                            GROUP.notes && (
                                <Card className="LStudio__app_vault_notes">
                                    <span className="LStudio__app_vault_notes_label">Notas personales</span>
                                    <div className="LStudio__app_drafts_item_content"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(GROUP.notes) }} />
                                </Card>
                            )
                        }

                        {
                            ALL_ENTRIES.length > 0 && (
                                <div className="LStudio__app_panel_bar">
                                    <ViewToolbar
                                        filters={<>
                                            <SearchFilter value={useSearch} onChange={setSearch}
                                                total={ALL_ENTRIES.length} matches={ENTRIES.length}
                                                placeholder="Título, correo, usuario, host o comando" />
                                            <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{ADVANCED_COUNT ? `Avanzado (${ADVANCED_COUNT})` : "Avanzado"}</AppButton>
                                        </>}
                                        expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={ADVANCED_COUNT} onClear={() => { setTypeFilter("ALL"); setFavoriteFilter("ALL"); setEnvironmentFilter(""); }}>
                                            <FieldSelect id="personal_vault_entries_adv_type" label="Tipo" value={useTypeFilter} onChange={(event) => setTypeFilter(event.target.value)} options={[{ value: "ALL", label: "Todos los tipos" }, ...OPTIONS_PERSONAL_VAULT_ENTRY_TYPE]} isSearchable />
                                            <FieldInput id="personal_vault_entries_adv_environment" label="Entorno" value={useEnvironmentFilter} onChange={(event) => setEnvironmentFilter(event.target.value)} placeholder="Producción, local..." />
                                            <FieldSelect id="personal_vault_entries_adv_favorite" label="Destacado" value={useFavoriteFilter} onChange={(event) => setFavoriteFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                                        </AdvancedFilterPanel>}
                                        total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                                        exportColumns={[
                                            { key: "title", label: "Acceso" },
                                            { key: "type", label: "Tipo" },
                                            { key: "environment", label: "Entorno" },
                                            { key: "email", label: "Correo" },
                                            { key: "username", label: "Usuario" },
                                            { key: "host", label: "Host" },
                                        ]}
                                        exportRows={ENTRIES.map((entry) => ({
                                            title: entry.title,
                                            type: onFindOptionLabel(OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, entry.type),
                                            environment: entry.environment ?? "",
                                            email: entry.email ?? "",
                                            username: entry.username ?? "",
                                            host: entry.host ?? "",
                                        }))}
                                        exportName="accesos" />
                                </div>
                            )
                        }

                        {
                            ENTRIES.length === 0 && useSearch ? (
                                <EmptyState icon={<FaShieldAlt />} title="Sin coincidencias"
                                    description={`Ningún acceso coincide con "${useSearch}".`} />
                            ) : ENTRIES.length === 0 ? (
                                <EmptyState icon={<FaShieldAlt />} title="Este grupo no tiene accesos"
                                    description="Cargá el primer acceso con sus datos, contraseña y notas de recuperación."
                                    action={
                                        <AppButton icon={<FaPlus />} onClick={() => onOpenCreate()}>
                                            Nuevo acceso
                                        </AppButton>
                                    } />
                            ) : (
                                <div className="LStudio__app_vault_entries LStudio__app_credentials_grid LStudio__app_vault_entries--project-parity">
                                    {
                                        PAGER.paged.map((entry) => {

                                            const REVEALED = useRevealed[entry.id];
                                            const VISIBLE_ROWS = ROWS.filter((row) => row.isSecret
                                                ? !!entry[`has${row.field.charAt(0).toUpperCase()}${row.field.slice(1)}`]
                                                : String(entry[row.field] ?? "").trim() !== "");
                                            const PRIMARY_ROWS = VISIBLE_ROWS.slice(0, 5);
                                            const DETAIL_ROWS = VISIBLE_ROWS.slice(5);
                                            const EXTRA_FIELDS = (entry.extra ?? []).filter((field) => String(field?.value ?? "").trim() !== "");
                                            const ENTRY_FILES = entry.files ?? [];
                                            const DISPLAY_FILES = ENTRY_FILES.filter((file) => !["RECOVERY_KIT", "EMERGENCY_FILE"].includes(file.purpose));
                                            const HAS_RECOVERY = (entry.recoveryCodes?.length ?? 0) > 0
                                                || ENTRY_FILES.some((file) => ["RECOVERY_KIT", "EMERGENCY_FILE"].includes(file.purpose));
                                            const HAS_REVEALABLE = VISIBLE_ROWS.some((row) => row.isSecret)
                                                || EXTRA_FIELDS.some((field) => !!field.isSecret);
                                            const IS_EXPANDED = !!useCardExpanded[entry.id];
                                            const HAS_DETAILS = DETAIL_ROWS.length > 0
                                                || EXTRA_FIELDS.length > 0
                                                || !!entry.command || !!entry.notes
                                                || DISPLAY_FILES.length > 0 || HAS_RECOVERY;

                                            const onRenderMainRow = (row) => {
                                                const VALUE = row.isSecret && REVEALED ? REVEALED[row.field] : entry[row.field];
                                                const CAN_COPY = !row.isSecret || !!REVEALED;
                                                return (
                                                    <div key={row.field} className="LStudio__app_credentials_item_row">
                                                        <span className="LStudio__app_credentials_item_key">{row.label}</span>
                                                        {CAN_COPY
                                                            ? <CopyValue value={VALUE} label={`Copiar ${row.label}`} />
                                                            : <span className="LStudio__app_credentials_item_hidden">{VALUE}</span>}
                                                    </div>
                                                );
                                            };

                                            return (
                                                <Card key={entry.id}
                                                    className="LStudio__app_vault_entry LStudio__app_credentials_item LStudio__app_vault_entry--credential"
                                                    data-level={0} data-favorite={!!entry.isFavorite}>

                                                    <div className="LStudio__app_vault_entry_head LStudio__app_credentials_item_head">
                                                        {entry.isFavorite && <FaStar className="LStudio__app_credentials_item_star" title="Destacada" />}
                                                        <span className="LStudio__app_vault_entry_title LStudio__app_credentials_item_title">{entry.title}</span>

                                                        <Badge tone="neutral" size="sm">
                                                            {onFindOptionLabel(OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, entry.type)}
                                                        </Badge>

                                                        {entry.environment && <Badge tone="accent" size="sm">{entry.environment}</Badge>}

                                                        {entry.authMode && entry.authMode !== "NONE" && (
                                                            <Badge tone="accent" size="sm">
                                                                2FA: {onFindOptionLabel(OPTIONS_PERSONAL_VAULT_AUTH_MODE, entry.authMode)}
                                                            </Badge>
                                                        )}

                                                        {entry.children?.length > 0 && (
                                                            <Badge tone="neutral" size="sm">
                                                                <FaSitemap /> {entry.children.length} hija{entry.children.length === 1 ? "" : "s"}
                                                            </Badge>
                                                        )}

                                                        <CardActionMenu
                                                            title="Acciones de credencial"
                                                            ariaLabel={`Opciones de ${entry.title}`}
                                                            items={[
                                                                {
                                                                    key: "favorite",
                                                                    icon: entry.isFavorite ? <FaRegStar /> : <FaStar />,
                                                                    label: entry.isFavorite ? "Quitar destacado" : "Destacar",
                                                                    onClick: () => onToggleFavorite(entry),
                                                                },
                                                                { key: "export", icon: <FaDownload />, label: "Guardar credencial...", onClick: () => onOpenExport(entry) },
                                                                { divider: true },
                                                                ...(entry.children?.length ? [{ key: "nested", icon: <FaSitemap />, label: `Ver hijas (${entry.children.length})`, onClick: () => setNestedEntryId(entry.id) }] : []),
                                                                { key: "child", icon: <FaPlus />, label: "Agregar hija", onClick: () => onOpenCreate(entry.id) },
                                                                { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => { setEntryEdit(entry); setParentDefault(null); setIsEntryOpen(true); } },
                                                                { divider: true },
                                                                { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveEntry(entry) },
                                                            ]}
                                                        />
                                                    </div>

                                                    {PRIMARY_ROWS.length > 0 && (
                                                        <div className="LStudio__app_vault_entry_body LStudio__app_credentials_item_body">
                                                            {PRIMARY_ROWS.map(onRenderMainRow)}
                                                        </div>
                                                    )}

                                                    {IS_EXPANDED && HAS_DETAILS && (
                                                        <div className="LStudio__app_credentials_details">
                                                            {DETAIL_ROWS.length > 0 && (
                                                                <div className="LStudio__app_vault_entry_body LStudio__app_credentials_item_body">
                                                                    {DETAIL_ROWS.map(onRenderMainRow)}
                                                                </div>
                                                            )}

                                                            {EXTRA_FIELDS.length > 0 && (
                                                                <div className="LStudio__app_vault_entry_body LStudio__app_credentials_item_body">
                                                                    {EXTRA_FIELDS.map((field, index) => (
                                                                        <div key={`${field.label}_${index}`} className="LStudio__app_credentials_item_row">
                                                                            <span className="LStudio__app_credentials_item_key">{field.label}</span>
                                                                            {!field.isSecret || REVEALED
                                                                                ? <CopyValue value={field.isSecret && REVEALED
                                                                                    ? (REVEALED.extra?.find((item) => item.label === field.label)?.value ?? field.value)
                                                                                    : field.value} label={`Copiar ${field.label}`} />
                                                                                : <span className="LStudio__app_credentials_item_hidden">{field.value}</span>}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {entry.command && (
                                                                <div className="LStudio__app_credentials_command">
                                                                    <AppCodeBlock code={entry.command} language={entry.commandLanguage}
                                                                        title="Comando de conexión" isPretty={false} />
                                                                </div>
                                                            )}

                                                            {DISPLAY_FILES.length > 0 && (
                                                                <div className="LStudio__app_credentials_files LStudio__app_file_collection">
                                                                    <div className="LStudio__app_file_collection_header">
                                                                        <span>{DISPLAY_FILES.length} archivo{DISPLAY_FILES.length === 1 ? "" : "s"}</span>
                                                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                                                            isLoading={useDownloadingFilesKey === `entry:${entry.id}`}
                                                                            onClick={() => onDownloadAllFiles(DISPLAY_FILES, entry.title, `entry:${entry.id}`)}>
                                                                            Descargar todos
                                                                        </AppButton>
                                                                    </div>
                                                                    <div className="LStudio__app_file_collection_scroll">
                                                                        {DISPLAY_FILES.map((file) => (
                                                                            <div key={file.id} className="LStudio__app_credentials_file">
                                                                                <FaFileAlt />
                                                                                <span className="LStudio__app_credentials_file_name">
                                                                                    <strong>Archivo</strong>
                                                                                    <small>{file.originalName || file.title}</small>
                                                                                </span>
                                                                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaExpand />}
                                                                                    aria-label="Vista previa" title="Vista previa" onClick={() => onOpenViewer(file)} />
                                                                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaDownload />}
                                                                                    aria-label="Descargar" title="Descargar" onClick={() => onDownload(file)} />
                                                                                <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                                                                    aria-label="Archivar archivo" title="Archivar archivo" onClick={() => onArchiveFile(file)} />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {entry.notes && (
                                                                <div className="LStudio__app_credentials_item_notes"
                                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.notes) }} />
                                                            )}

                                                            {HAS_RECOVERY && (
                                                                <PersonalVaultRecoveryPanel
                                                                    entry={entry}
                                                                    groupId={groupId}
                                                                    accessPin={useUnlockedPin}
                                                                    onChanged={(patch) => mutateLocal({
                                                                        id: GROUP.id,
                                                                        patch: { entries: onPatchEntryTree(GROUP.entries ?? [], entry.id, patch ?? {}) },
                                                                    })} />
                                                            )}
                                                        </div>
                                                    )}

                                                    {(HAS_REVEALABLE || HAS_DETAILS) && (
                                                        <div className="LStudio__app_credentials_item_actions">
                                                            {HAS_REVEALABLE && (
                                                                <AppButton size="sm" variant="ghost" tone="neutral"
                                                                    icon={REVEALED ? <FaEyeSlash /> : <FaEye />}
                                                                    onClick={() => onReveal(entry)}>
                                                                    {REVEALED ? "Ocultar" : "Revelar"}
                                                                </AppButton>
                                                            )}

                                                            {HAS_DETAILS && (
                                                                <AppButton size="sm" variant="ghost" tone="neutral"
                                                                    icon={IS_EXPANDED ? <FaChevronDown /> : <FaChevronRight />}
                                                                    onClick={() => setCardExpanded((current) => ({ ...current, [entry.id]: !current[entry.id] }))}>
                                                                    {IS_EXPANDED ? "Mostrar menos" : "Mostrar más"}
                                                                </AppButton>
                                                            )}
                                                        </div>
                                                    )}
                                                </Card>
                                            );
                                        })
                                    }
                                </div>
                            )
                        }

                        <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                            total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

                        {GROUP_FILES.length > 0 && (
                            <Card className="LStudio__app_vault_files_card LStudio__app_file_collection">
                                <div className="LStudio__app_file_collection_header">
                                    <span className="LStudio__app_vault_notes_label">Archivos del grupo</span>
                                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaDownload />}
                                        isLoading={useDownloadingFilesKey === "group"}
                                        onClick={() => onDownloadAllFiles(GROUP_FILES, GROUP.name, "group")}>
                                        Descargar todos
                                    </AppButton>
                                </div>
                                <div className="LStudio__app_vault_files_list LStudio__app_file_collection_scroll">
                                    {GROUP_FILES.map((file) => (
                                        <div key={file.id} className="LStudio__app_vault_file_row">
                                            <span className="LStudio__app_vault_file_thumb">
                                                {file.kind === "IMAGE" && !file.isEncrypted && file.url
                                                    ? <PrivateBlobMedia file={file} loadBlob={onLoadFileBlob} alt={file.title} />
                                                    : <FaFileAlt />}
                                            </span>
                                            <span className="LStudio__app_vault_file_name">{file.originalName}</span>
                                            <span className="LStudio__app_vault_file_size">
                                                {(Number(file.size) / 1024).toFixed(0)} KB
                                            </span>
                                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaExpand />}
                                                isIconOnly aria-label="Vista previa" title="Vista previa" onClick={() => onOpenViewer(file)} />
                                            <CardActionMenu title="Opciones del archivo"
                                                ariaLabel={`Opciones de ${file.originalName || file.title}`}
                                                items={[
                                                    { key: "download", icon: <FaDownload />, label: "Descargar", onClick: () => onDownload(file) },
                                                    { divider: true },
                                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveFile(file) },
                                                ]} />
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </>
                )
            }

            </div>

            <NestedVaultModal
                isOpen={!!NESTED_ENTRY}
                onClose={() => setNestedEntryId(null)}
                title={NESTED_ENTRY ? `Bóveda de credenciales · ${NESTED_ENTRY.title}` : "Bóveda de credenciales"}
                description="Una credencial principal puede tener varias hijas. Las hijas no pueden contener otras credenciales."
                parentTitle={NESTED_ENTRY?.title || "Credencial principal"}
                parentLabel="Credencial principal"
                childLabel="hija"
                childCount={(NESTED_ENTRY?.children ?? []).length}
                addLabel="Agregar hija"
                onAddChild={NESTED_ENTRY ? () => onOpenCreate(NESTED_ENTRY.id) : undefined}
                isChildFormOpen={useIsEntryOpen}>
                {(NESTED_ENTRY?.children ?? []).map((entry) => (
                    <VaultNestedEntryCard
                        key={entry.id}
                        entry={entry}
                        parentTitle={NESTED_ENTRY.title}
                        typeLabel={onFindOptionLabel(OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, entry.type)}
                        environmentLabel={entry.environment || ""}
                        rows={ROWS}
                        revealed={useRevealed[entry.id]}
                        onReveal={onReveal}
                        onFavorite={onToggleFavorite}
                        onExport={onOpenExport}
                        onEdit={(child) => { setEntryEdit(child); setParentDefault(null); setIsEntryOpen(true); }}
                        onArchive={onArchiveEntry}
                        onOpenFile={onOpenViewer}
                        loadBlob={onLoadFileBlob}
                        extraBadges={entry.authMode && entry.authMode !== "NONE" ? (
                            <Badge tone="accent" size="sm">
                                2FA: {onFindOptionLabel(OPTIONS_PERSONAL_VAULT_AUTH_MODE, entry.authMode)}
                            </Badge>
                        ) : null}
                        afterContent={(entry.recoveryCodes?.length > 0 || (entry.files ?? []).some((file) => ["RECOVERY_KIT", "EMERGENCY_FILE"].includes(file.purpose))) ? (
                            <PersonalVaultRecoveryPanel
                                entry={entry}
                                groupId={groupId}
                                accessPin={useUnlockedPin}
                                onChanged={(patch) => mutateLocal({
                                    id: GROUP.id,
                                    patch: { entries: onPatchEntryTree(GROUP.entries ?? [], entry.id, patch ?? {}) },
                                })} />
                        ) : null}
                    />
                ))}
            </NestedVaultModal>

            <AppModal
                isOpen={useIsGroupFilesOpen}
                onClose={() => { setIsGroupFilesOpen(false); setFiles([]); }}
                size="md"
                title="Agregar archivos al grupo"
                description="Los controles de carga viven dentro de esta acción. La ficha normal sólo muestra archivos que ya fueron guardados."
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => { setIsGroupFilesOpen(false); setFiles([]); }}>Cancelar</AppButton>
                        <AppButton icon={<FaUpload />} isLoading={useIsUploading} isDisabled={useFiles.length === 0}
                            onClick={async () => { if (await onUpload()) setIsGroupFilesOpen(false); }}>
                            Guardar archivos {useFiles.length > 0 ? `(${useFiles.length})` : ""}
                        </AppButton>
                    </>
                }>
                <FieldFile id="vault_group_files" label="Seleccionar archivos" files={useFiles} onChange={setFiles}
                    dropText="Capturas, comprobantes, licencias o documentos personales" />
            </AppModal>

            <UniversalFilePreviewModal
                file={usePreviewFile}
                isOpen={!!usePreviewFile}
                onClose={() => setPreviewFile(null)}
                loadBlob={onLoadFileBlob}
                onDownload={onDownload}
            />

            <CredentialExportPickerModal
                isOpen={!!useExportEntry}
                title={useExportEntry ? `Guardar / exportar · ${useExportEntry.entry.title}` : "Guardar / exportar"}
                rows={useExportEntry?.rows ?? []}
                onClose={() => setExportEntry(null)}
                onExport={onExportCredential}
            />

            <PersonalVaultEntryFormModal
                isOpen={useIsEntryOpen}
                isStacked={!!NESTED_ENTRY}
                groupId={groupId}
                accessPin={useUnlockedPin}
                entry={useEntryEdit}
                revealed={useEntryEdit ? useRevealed[useEntryEdit.id] : null}
                parentOptions={PARENT_OPTIONS}
                defaultParentId={useParentDefault}
                onClose={() => { setIsEntryOpen(false); setEntryEdit(null); setParentDefault(null); }}
                onSaved={onEntrySavedLocal} />
        </ViewLayout>
    );
};
